import React, { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  ITabs,
  MuiBox,
  MuiTabPanel,
  MuiTabs,
  WarningConfirmationModal,
} from "../../../../components";
import {
  getParsedBEErrors,
  hasKey,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateClientActivitiesAndDeliverablesWrapper } from "./ClientActivitiesAndDeliverables.style";
import {
  EditActivity,
  EditDeliverable,
  ViewActivity,
  ViewDeliverables,
} from "./components";
import {
  ActivityAndDeliverablesFiltersTypes,
  ActivityType,
  CreateClientActivitiesAndDeliverablesProps,
  CreateClientActivitiesFormKeys,
  CreateClientActivitiesRowValue,
  CreateClientDeliverablesFormKeys,
  CreateClientDeliverablesRowValue,
  DeliverableUniqueFiltersTypes,
} from "./modelsAndSchemas/models";

import {
  useDeleteCreateClientActivityMutation,
  useDeleteCreateClientDeliverableMutation,
} from "../../../../api/services/client/clientApis";

import { useDispatch, useSelector } from "react-redux";
import {
  BE_ERROR_CODES,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../helpers/constants";
import {
  commonSiteCoreKeys,
  UnitTypesKey,
} from "../../../../helpers/constants/siteCoreKeys";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";

import { ErrorModel } from "../../../../api/services/client/models";
import { ButtonWithIcon } from "../../../../components/buttonWithIcon/ButtonWithIcon";
import { LabelValue } from "../../../../helpers/types/common";
import { CreateClientActivitiesTab } from "./tabs/CreateClientActivitiesTab";
import { CreateClientDeliverablesTab } from "./tabs/CreateClientDeliverablesTab";
import { usePermissions } from "../../../../hooks/usePermissions";

export const pageId = PAGE_IDS.CREATE_CLIENT_ACTIVITY_DELIVERABLES;

export const CreateClientActivitiesAndDeliverables: FC<
  CreateClientActivitiesAndDeliverablesProps
> = ({
  rows,
  deliverableRows,
  onCreateActivityClick,
  successHandler,
  successHandlerDeliverables,
  isView = false,
  onCreateDeliverableClick,
}) => {
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const deliverableTableRef = useRef<{ resetPagination: () => void }>(null);
  const [showViewActivity, setShowViewActivity] = useState(false);
  const [showEditActivity, setShowEditActivity] = useState(false);
  const [showEditDeliverables, setShowEditDeliverables] = useState(false);
  const [showViewDeliverables, setShowViewDeliverables] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(
    {} as CreateClientActivitiesRowValue,
  );
  const [selectedDeliverables, setSelectedDeliverables] = useState(
    {} as CreateClientDeliverablesRowValue,
  );

  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { hasModulePermission } = usePermissions();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  // updated sitecore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    CreateClientActivitiesRowValue[]
  >([]);

  const [allUpdateDeliverablesRows, setAllUpdateDeliverablesRows] = useState<
    CreateClientDeliverablesRowValue[]
  >([]);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [filteredDeliverableResults, setFilteredDeliverableResults] = useState([
    ...deliverableRows,
  ]);
  const [showModal, setShowModal] = useState(false);
  const [showModalFor, setShowModalFor] = useState("");
  const [showWarningAlreadyInUseActivity, setShowWarningAlreadyInUseActivity] =
    useState(false);

  const [uniqueFilters, setUniqueFilters] =
    useState<ActivityAndDeliverablesFiltersTypes>({
      [CreateClientActivitiesFormKeys.activityType]: [],
      [CreateClientActivitiesFormKeys.modality]: [],
      [CreateClientActivitiesFormKeys.units]: [],
    });

  const [uniqueDeliverablesFilters, setUniqueDeliverablesFilters] =
    useState<DeliverableUniqueFiltersTypes>({
      [CreateClientDeliverablesFormKeys.activityTypes]: [],
      [CreateClientDeliverablesFormKeys.deliverableType]: [],
      [CreateClientDeliverablesFormKeys.units]: [],
    });

  const dispatch = useDispatch();

  const {
    mutateAsync: deleteCreateClientActivity,
    isSuccess,
    isPending: isDeleteActivityPending,
    isError: isDeleteClientActivityError,
    error: deleteClientActivityError,
  } = useDeleteCreateClientActivityMutation();

  const {
    mutateAsync: deleteCreateClientDeliverable,
    isSuccess: isSuccessDeleteDeliverable,
    isPending: isDeleteDeliverablePending,
    isError: isDeleteDeliverableError,
  } = useDeleteCreateClientDeliverableMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeleteActivityPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isDeleteActivityPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isDeleteDeliverablePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isDeleteDeliverablePending]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.activitiesList,
            activitiesAndDeliverablesSiteCoreKeys.activityDeletedSuccessfully,
          ),
        }),
      );
      successHandler();
    }
  }, [isSuccess]);

  const showErrorsFromBE = (error: ErrorModel) => {
    if (
      error.errorCode &&
      error.errorCode ===
        BE_ERROR_CODES.ACTIVITY_ALREADY_IN_USE_CLIENT_SPECIFIC_ACTIVITY
    ) {
      setShowWarningAlreadyInUseActivity(true);
    }
  };

  useEffect(() => {
    dispatch(toggleLoader({ isVisible: false }));
    if (isDeleteClientActivityError && deleteClientActivityError) {
      const error = getParsedBEErrors(deleteClientActivityError)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
      } else {
        dispatch(
          toggleToast({
            open: true,
            message: commonLabels.someThingWentWrongPleaseTryAgainLater,
            type: "error",
          }),
        );
      }
    }
  }, [isDeleteClientActivityError]);

  useEffect(() => {
    if (isSuccessDeleteDeliverable) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
            activitiesAndDeliverablesSiteCoreKeys.deleteDeliverableSuccess,
          ),
        }),
      );
      successHandlerDeliverables();
    }
    if (isDeleteDeliverableError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSuccessDeleteDeliverable, isDeleteDeliverableError]);

  const handleWarningPopup = () => {
    if (showWarningAlreadyInUseActivity) {
      setShowWarningAlreadyInUseActivity(false);
    }
  };

  const resetActivitiesPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };
  const resetDeliverablesPagination = () => {
    if (deliverableTableRef.current) {
      deliverableTableRef.current.resetPagination();
    }
  };

  const indexRef = useRef<number>();

  const getValueByKeyOfDropdown = (
    masterKey: string,
    parentKey: string,
    key: string,
  ) => {
    return fetchValueByKeyOfDropdown(masterKey, parentKey, key);
  };

  const getUniqueFilters = (
    data: CreateClientActivitiesRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((item: CreateClientActivitiesRowValue) => item[key]) // Filter out null or undefined values
          .map((item: CreateClientActivitiesRowValue) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  const getUniqueDeliverablesFilters = (
    data: CreateClientDeliverablesRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((item: CreateClientDeliverablesRowValue) => item[key]) // Filter out null or undefined values
          .map((item: CreateClientDeliverablesRowValue) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  const { control, watch, reset, getValues } = useForm({
    mode: "all",
    defaultValues: {
      [CreateClientActivitiesFormKeys.activityType]: "",
      [CreateClientActivitiesFormKeys.modality]: "",
      [CreateClientActivitiesFormKeys.topic]: "",
      [CreateClientActivitiesFormKeys.activityStatus]: "",
      [CreateClientActivitiesFormKeys.units]: "",
      [CreateClientActivitiesFormKeys.measurement]: "",
      [CreateClientActivitiesFormKeys.candidateStatuses]: "",
    },
  });

  const {
    control: deliverableControl,
    watch: deliverableWatch,
    reset: deliverableReset,
    getValues: deliverableGetValues,
  } = useForm({
    mode: "all",
    defaultValues: {
      [CreateClientDeliverablesFormKeys.activityTypes]: "",
      [CreateClientDeliverablesFormKeys.name]: "",
      [CreateClientDeliverablesFormKeys.deliverableType]: "",
      [CreateClientDeliverablesFormKeys.units]: "",
      [CreateClientDeliverablesFormKeys.measurements]: "",
    },
  });

  const dropdownKeyMapping = {
    modality: activitiesAndDeliverablesSiteCoreKeys.listModalityList,
    candidateStatuses:
      activitiesAndDeliverablesSiteCoreKeys.listParticipantStatusList,
    activityStatus: activitiesAndDeliverablesSiteCoreKeys.listStatusList,
    measurement: activitiesAndDeliverablesSiteCoreKeys.listMinutesList,
    units: activitiesAndDeliverablesSiteCoreKeys.listUnitList,
  };

  const deliverableDropdownKeyMapping = {
    deliverableType: activitiesAndDeliverablesSiteCoreKeys.deliverableTypeList,
    measurements: activitiesAndDeliverablesSiteCoreKeys.listMinutesList,
    units: activitiesAndDeliverablesSiteCoreKeys.createDeliverableUnitList,
  };

  const getDropdownValueForKey = (
    key: keyof typeof dropdownKeyMapping,
    data: CreateClientActivitiesRowValue,
  ) => {
    const value = data[key];
    if (key === CreateClientActivitiesFormKeys.measurement) {
      if (data.units === UnitTypesKey.Minutes) {
        return Number(
          getValueByKeyOfDropdown(
            activitiesAndDeliverablesSiteCoreKeys.activitiesList,
            dropdownKeyMapping[key],
            value,
          ),
        );
      } else {
        return Number(value);
      }
    }
    if (
      key === CreateClientActivitiesFormKeys.candidateStatuses ||
      key === CreateClientActivitiesFormKeys.modality
    ) {
      if (Array.isArray(data[key])) {
        return (value as string[]).map((item) =>
          getValueByKeyOfDropdown(
            activitiesAndDeliverablesSiteCoreKeys.activitiesList,
            dropdownKeyMapping[key],
            item,
          ),
        );
      }
    } else {
      return getValueByKeyOfDropdown(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        dropdownKeyMapping[key],
        data[key],
      );
    }
  };

  const getDeliverablesDropdownValueForKey = (
    key: keyof typeof deliverableDropdownKeyMapping,
    data: CreateClientDeliverablesRowValue,
  ) => {
    if (key === CreateClientDeliverablesFormKeys.measurements) {
      if (data.units === UnitTypesKey.Minutes) {
        return Number(
          getValueByKeyOfDropdown(
            activitiesAndDeliverablesSiteCoreKeys.activitiesList,
            deliverableDropdownKeyMapping[key],
            data[key],
          ),
        );
      } else {
        return Number(data[key]);
      }
    }

    return getValueByKeyOfDropdown(
      activitiesAndDeliverablesSiteCoreKeys.createDeliverableKey,
      deliverableDropdownKeyMapping[key],
      data[key],
    );
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: CreateClientActivitiesRowValue[] = JSON.parse(
        JSON.stringify(rows),
      );
      localData.forEach((data) => {
        data.activityStatusKey = data.activityStatus;
        data.candidateStatusKey = data.candidateStatuses;
        data.modality = data.modalities;
        Object.keys(dropdownKeyMapping).forEach((key) => {
          data[key] = getDropdownValueForKey(
            key as keyof typeof dropdownKeyMapping,
            data,
          );
        });
      });

      setFilteredResults([...localData]);
      setAllUpdatedRows(localData);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [CreateClientActivitiesFormKeys.activityType]: getUniqueFilters(
          localData,
          CreateClientActivitiesFormKeys.activityType,
        ),
        [CreateClientActivitiesFormKeys.modality]: getUniqueFilters(
          localData,
          CreateClientActivitiesFormKeys.modality,
        ),
        [CreateClientActivitiesFormKeys.units]: getUniqueFilters(
          localData,
          CreateClientActivitiesFormKeys.units,
        ),
      }));

      reset();
      resetActivitiesPagination();
    }
  }, [rows, siteCoreLoading]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData: CreateClientDeliverablesRowValue[] = JSON.parse(
        JSON.stringify(deliverableRows),
      );

      localData.forEach((data) => {
        Object.keys(deliverableDropdownKeyMapping).forEach((key) => {
          data[key] = getDeliverablesDropdownValueForKey(
            key as keyof typeof deliverableDropdownKeyMapping,
            data,
          );
        });
      });

      setFilteredDeliverableResults([...localData]);
      setAllUpdateDeliverablesRows(localData);

      const findUniqueValues = (
        data: CreateClientDeliverablesRowValue[],
        column: string,
      ): any => {
        const uniqueRecords = data
          .filter((item) => item[column]?.length > 0)
          .flatMap((item) =>
            item[column].map((activityType: ActivityType) => activityType.name),
          );

        const resultSet = [...new Set(uniqueRecords)].map((value) => ({
          value,
          label: fetchValueByKeyOfDropdown(
            activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
            activitiesAndDeliverablesSiteCoreKeys.activityTypeList,
            value,
          ),
        }));

        return resultSet;
      };

      setUniqueDeliverablesFilters((prevFilters) => ({
        ...prevFilters,
        [CreateClientDeliverablesFormKeys.activityTypes]: findUniqueValues(
          localData,
          CreateClientDeliverablesFormKeys.activityTypes,
        ),
        [CreateClientDeliverablesFormKeys.deliverableType]:
          getUniqueDeliverablesFilters(
            localData,
            CreateClientDeliverablesFormKeys.deliverableType,
          ),
        [CreateClientDeliverablesFormKeys.name]: getUniqueDeliverablesFilters(
          localData,
          CreateClientDeliverablesFormKeys.name,
        ),
        [CreateClientActivitiesFormKeys.units]: getUniqueDeliverablesFilters(
          localData,
          CreateClientActivitiesFormKeys.units,
        ),
      }));

      deliverableReset();
      resetDeliverablesPagination();
    }
  }, [deliverableRows, siteCoreLoading]);

  const applyFilters = (data: CreateClientActivitiesRowValue[]) => {
    const filters = getValues();

    const activityStatusAll = fetchValueByKeyOfDropdown(
      activitiesAndDeliverablesSiteCoreKeys.activitiesList,
      activitiesAndDeliverablesSiteCoreKeys.listStatusList,
      commonSiteCoreKeys.statusAll,
    );

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        // Skip empty filters
        if (filterValue === "") return true;

        // Handle special case for activityStatus
        if (key === CreateClientActivitiesFormKeys.activityStatus) {
          const statusMatch =
            filterValueStr === "" ||
            filterValueStr === activityStatusAll.toLowerCase() ||
            item[key]?.toString().toLowerCase() === filterValueStr;

          return statusMatch;
        } else if (key === CreateClientActivitiesFormKeys.candidateStatuses) {
          return item[key]?.some(
            (status: string) => status.toLowerCase() === filterValueStr,
          );
        }

        const itemValue = item[key]?.toString().toLowerCase();

        return itemValue?.includes(filterValueStr);
      });
    });
  };

  const applyDeliverablesFilters = (
    data: CreateClientDeliverablesRowValue[],
  ) => {
    const filters = deliverableGetValues();

    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key as keyof typeof filters];

        const filterValueStr = filterValue.toString().toLowerCase();

        if (item[key] === undefined && filterValue !== "") {
          return false;
        }

        // Skip empty filters
        if (filterValue === "") return true;

        if (key === CreateClientDeliverablesFormKeys.activityTypes) {
          return item[key]?.some((activityType: ActivityType) =>
            activityType.name?.toLowerCase().includes(filterValueStr),
          );
        } else {
          const itemValue = item[key]?.toString().toLowerCase();
          return itemValue?.includes(filterValueStr);
        }
      });
    });
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const filteredData = applyFilters(allUpdatedRows);
      setFilteredResults(filteredData);
      resetActivitiesPagination();
    }
  }, [
    watch(CreateClientActivitiesFormKeys.activityType),
    watch(CreateClientActivitiesFormKeys.modality),
    watch(CreateClientActivitiesFormKeys.topic),
    watch(CreateClientActivitiesFormKeys.activityStatus),
    watch(CreateClientActivitiesFormKeys.units),
    watch(CreateClientActivitiesFormKeys.candidateStatuses),
  ]);

  useEffect(() => {
    if (isNotEmpty(allUpdateDeliverablesRows)) {
      const filteredData = applyDeliverablesFilters(allUpdateDeliverablesRows);
      setFilteredDeliverableResults(filteredData);
      resetDeliverablesPagination();
    }
  }, [
    deliverableWatch(CreateClientDeliverablesFormKeys.name),
    deliverableWatch(CreateClientDeliverablesFormKeys.deliverableType),
    deliverableWatch(CreateClientDeliverablesFormKeys.units),
    deliverableWatch(CreateClientDeliverablesFormKeys.activityTypes),
  ]);

  const onActivityViewClick = (
    data: CreateClientActivitiesRowValue,
    isEdit = false,
  ) => {
    setSelectedActivity(data);
    isEdit ? setShowEditActivity(true) : setShowViewActivity(true);
  };

  const onDeliverableViewClick = (
    data: CreateClientDeliverablesRowValue,
    isEdit = false,
  ) => {
    setSelectedDeliverables(data);
    isEdit ? setShowEditDeliverables(true) : setShowViewDeliverables(true);
  };

  const [selectedTab, setSelectedTab] = useState(0);

  const tabIdActivity = `${pageId}-tab1`;
  const tabIdDeliverables = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdActivity,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.activityTabText,
      ),
    },
    {
      id: tabIdDeliverables,
      label: fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.deliverablesTabText,
      ),
    },
  ];

  const onEditDeliverableClick = () => {
    setShowViewDeliverables(false);
    setShowEditDeliverables(true);
  };

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  const handleModalClose = () => {
    if (showModal) {
      setShowModal(false);
      setShowModalFor("");
    }
  };

  const handleDeleteClick = () => {
    if (isNotEmpty(indexRef.current)) {
      let idToDelete = indexRef.current;

      if (showModalFor === "Activity") {
        deleteCreateClientActivity({
          activityId: idToDelete,
        });
      } else if (showModalFor === "Deliverable") {
        deleteCreateClientDeliverable({
          deliverableId: idToDelete,
        });
      }

      setShowModal(false);
      setShowModalFor("");
    }
  };

  const deleteActivity = (row: any) => {
    if (showModal === false) {
      indexRef.current = row?.id;
      setShowModal(true);
      setShowModalFor("Activity");
    }
  };

  const deleteDeliverable = (row: any) => {
    if (showModal === false) {
      indexRef.current = row?.id;
      setShowModal(true);
      setShowModalFor("Deliverable");
    }
  };

  const onEditActivityClick = () => {
    setShowViewActivity(false);
    setShowEditActivity(true);
  };

  const errorWarning = () => {
    if (showModalFor === "Activity") {
      return fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.activitiesList,
        activitiesAndDeliverablesSiteCoreKeys.deleteActivityConfirmation,
      );
    } else if (showModalFor === "Deliverable") {
      return fetchLabel(
        activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
        activitiesAndDeliverablesSiteCoreKeys.deleteDeliverableConfirmation,
      );
    }
    return "";
  };

  return (
    <CreateClientActivitiesAndDeliverablesWrapper>
      <MuiTabs
        id="activities-deliverables"
        tabs={tabs}
        value={selectedTab}
        onChange={onTabChange}
        tabsTestIds={[tabIdActivity, tabIdDeliverables]}
      >
        <MuiTabPanel
          panelId={`${pageId}-panel1`}
          value={selectedTab}
          index={0}
          ariaLabelledById={tabIdActivity}
        >
          {!isView &&
            hasModulePermission(
              MODULE_PERMISSIONS.ADD_CLIENT_SPECIFIC_ACTIVITIES,
            ) && (
              <MuiBox className="semi-header">
                <ButtonWithIcon
                  data-testid="create-activity"
                  buttonText={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.activitiesList,
                    activitiesAndDeliverablesSiteCoreKeys.createActivity,
                  )}
                  onActionClick={onCreateActivityClick}
                />
              </MuiBox>
            )}

          <CreateClientActivitiesTab
            tableRef={tableRef}
            control={control}
            isView={isView}
            onActivityViewClick={onActivityViewClick}
            filteredResults={filteredResults}
            rows={rows}
            uniqueFilters={uniqueFilters}
            deleteAction={deleteActivity}
          ></CreateClientActivitiesTab>
        </MuiTabPanel>

        <MuiTabPanel
          panelId={`${pageId}-panel2`}
          value={selectedTab}
          index={1}
          ariaLabelledById={tabIdDeliverables}
        >
          {!isView &&
            hasModulePermission(
              MODULE_PERMISSIONS.ADD_CLIENT_SPECIFIC_ACTIVITIES,
            ) && (
              <MuiBox className="semi-header">
                <ButtonWithIcon
                  data-testid="create-deliverables"
                  buttonText={fetchLabel(
                    activitiesAndDeliverablesSiteCoreKeys.deliverablesListKey,
                    activitiesAndDeliverablesSiteCoreKeys.addDeliverablesLabel,
                  )}
                  onActionClick={onCreateDeliverableClick}
                />
              </MuiBox>
            )}
          <CreateClientDeliverablesTab
            tableRef={deliverableTableRef}
            control={deliverableControl}
            isView={isView}
            onDeliverableViewClick={onDeliverableViewClick}
            filteredResults={filteredDeliverableResults}
            rows={deliverableRows}
            uniqueFilters={uniqueDeliverablesFilters}
            deleteAction={deleteDeliverable}
          ></CreateClientDeliverablesTab>
        </MuiTabPanel>
      </MuiTabs>

      {showViewActivity && (
        <ViewActivity
          show={showViewActivity}
          selectedActivity={selectedActivity}
          handleCloseModal={() => setShowViewActivity(false)}
          isView={isView}
          editButtonClick={onEditActivityClick}
        />
      )}
      {showEditActivity && (
        <EditActivity
          show={showEditActivity}
          selectedActivity={selectedActivity}
          handleCloseModal={() => setShowEditActivity(false)}
          pageId={pageId}
          getListOfActivitiesApi={successHandler}
        />
      )}

      {showViewDeliverables && (
        <ViewDeliverables
          show={showViewDeliverables}
          pageId={pageId}
          handleCloseModal={() => setShowViewDeliverables(false)}
          isView={isView}
          editButtonClick={() => onEditDeliverableClick()}
          selectedDeliverableId={selectedDeliverables?.id}
        />
      )}
      {showEditDeliverables && (
        <EditDeliverable
          show={showEditDeliverables}
          pageId={pageId}
          handleCloseModal={() => setShowEditDeliverables(false)}
          selectedDeliverables={selectedDeliverables}
          callApiFunction={successHandlerDeliverables}
        />
      )}

      {showModal && (
        <WarningConfirmationModal
          show={showModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleModalClose}
          onPrimaryButtonClick={handleDeleteClick}
          labelContent={errorWarning()}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      {showWarningAlreadyInUseActivity && (
        <WarningConfirmationModal
          show={showWarningAlreadyInUseActivity}
          onClose={() => {
            setShowWarningAlreadyInUseActivity(false);
          }}
          onPrimaryButtonClick={handleWarningPopup}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.activitiesList,
            activitiesAndDeliverablesSiteCoreKeys.thisActivityCannotBeDeletedBecauseItIsTaggedToAnExistingProgram,
          )}
        />
      )}
    </CreateClientActivitiesAndDeliverablesWrapper>
  );
};
